// src/lib/variables-secondaires-nues.js — UNE VARIABLE FACULTATIVE DOIT VIVRE
// SOUS SON PROPRE {#if}, SINON LA PHRASE PARTIRAIT SANS ELLE.
//
// ⚠️ SOURCE UNIQUE (pas un miroir) : CJS pur, consommé côté CLIENT par
// l'éditeur de gabarits (refus AVANT le clic, message identique) et côté
// SERVEUR via le shim `lib/variables-secondaires-nues.js` par l'action
// `message_save` — la barrière qui compte. Même doctrine que
// src/lib/sms-validator.js (CRA interdit l'import de lib/ depuis src/, le
// require inverse est légal, un miroir dupliqué serait un risque de drift).
//
// POURQUOI : depuis la garde étroite (variables-essentielles.js), une variable
// SECONDAIRE vide ne retient plus l'envoi, elle s'efface. « Nous vous
// souhaitons un excellent séjour à {ville} 🌸 » partirait donc « …séjour à 🌸 »
// (règle Dimitri : cette phrase ne doit pas pouvoir partir). Le moteur ne sait
// effacer qu'un token ; c'est le GABARIT qui doit masquer la phrase entière :
// « …séjour{#if ville} à {ville}{/if} 🌸 ». Un test structurel le vérifie sur
// le snapshot prod, mais un admin qui retire le {#if ville} en base ferait
// repartir la phrase — d'où ce détecteur au SAVE.
//
// RÈGLE : chaque occurrence `{k}` d'une secondaire doit être à l'intérieur
// d'un bloc `{#if k}…{/if}` ou `{#if k ET …}…{/if}` (masqué à coup sûr quand
// k est vide). `{#if k OU x}` ne couvre pas (le bloc peut s'afficher k vide) ;
// `{#if autre}` non plus. Les essentielles ne sont pas le sujet : vides, elles
// retiennent l'envoi (skipped_missing_vars), jamais de phrase tronquée.

const { VARIABLES_SECONDAIRES } = require("./variables-essentielles.js");

const _SECONDAIRES = new Set(VARIABLES_SECONDAIRES);
// Même grammaire que IF_BLOCK_RE / evalIfExpression (template-variables.js) :
// single-var ou cumul homogène ET/OU, pas de nesting.
const _BLOC_RE = /\{#if\s+([a-zA-Z_][a-zA-Z0-9_ \t]*)\}([\s\S]*?)\{\/if\}/g;
const _VAR_RE = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g;

// Les 6 champs texte d'un gabarit Plateformes (table `messages`).
const CHAMPS_GABARIT = Object.freeze([
  "subject_fr",
  "subject_en",
  "subject_es",
  "body_fr",
  "body_en",
  "body_es",
]);

// Blocs {#if}…{/if} : plage [debut, fin[ + variables qui, vides, masquent le
// bloc à coup sûr (single-var, ou cumul ET). Un OU ou une expression
// malformée ne couvre rien.
function _blocsCouvrants(tpl) {
  const blocs = [];
  for (const m of tpl.matchAll(_BLOC_RE)) {
    const jetons = m[1].trim().split(/\s+/);
    const ops = jetons.filter((_, i) => i % 2 === 1);
    const vars = jetons.filter((_, i) => i % 2 === 0);
    const couvre =
      jetons.length % 2 === 1 &&
      (ops.length === 0 || ops.every((o) => o === "ET"));
    blocs.push({
      debut: m.index,
      fin: m.index + m[0].length,
      vars: couvre ? new Set(vars) : new Set(),
    });
  }
  return blocs;
}

/**
 * Variables SECONDAIRES référencées hors d'un bloc {#if <elle-même>}…{/if},
 * dédupliquées, dans l'ordre d'apparition. Texte vide/absent ⇒ [].
 *
 * @param {string} tpl
 * @returns {string[]}
 */
function secondairesNues(tpl) {
  if (typeof tpl !== "string" || tpl === "") return [];
  const blocs = _blocsCouvrants(tpl);
  const nues = new Set();
  for (const m of tpl.matchAll(_VAR_RE)) {
    const key = m[1];
    if (!_SECONDAIRES.has(key)) continue;
    const couverte = blocs.some(
      (b) => m.index > b.debut && m.index < b.fin && b.vars.has(key),
    );
    if (!couverte) nues.add(key);
  }
  return Array.from(nues);
}

/**
 * Le message montré à l'admin — identique côté client et côté serveur.
 */
function messageSecondaireNue(variable) {
  return (
    `La variable {${variable}} est facultative : entoure-la de ` +
    `{#if ${variable}}…{/if}, sinon la phrase partirait sans elle.`
  );
}

/**
 * Vérifie les 6 champs d'un gabarit. `ok:false` dès qu'une secondaire est nue
 * quelque part ; `erreurs` nomme le champ, la variable et le message.
 *
 * @param {object} gabarit — { subject_fr, …, body_es }
 * @returns {{ ok: boolean, erreurs: Array<{champ: string, variable: string, message: string}> }}
 */
function verifierSecondairesGabarit(gabarit) {
  const erreurs = [];
  const g = gabarit || {};
  for (const champ of CHAMPS_GABARIT) {
    for (const variable of secondairesNues(g[champ])) {
      erreurs.push({
        champ,
        variable,
        message: messageSecondaireNue(variable),
      });
    }
  }
  return { ok: erreurs.length === 0, erreurs };
}

module.exports = {
  CHAMPS_GABARIT,
  secondairesNues,
  messageSecondaireNue,
  verifierSecondairesGabarit,
};
